import React from 'react';
import { theme } from '../../../theme';
import { styles } from './dashboardStyles';

const getFriendlyActionLabel = (events, eventId, actionId) => {
    const ev = events.find(e => e._id === eventId);
    if (!ev) return actionId;
    const act = ev.allowedActions.find(a => a.actionId === actionId);
    return act ? act.label : actionId;
};

const getUnitInfo = (events, eventId, actionId, units) => {
    const ev = events.find(e => e._id === eventId);
    if (!ev) return units;
    const act = ev.allowedActions.find(a => a.actionId === actionId);
    if (!act) return units;

    if (act.unit === 'ha') return 'Field Area';
    return `${units} ${act.unit}`;
};

// Per-bid rate (e.g. "£653.1/Ha") for the "Price per Unit" column — mirrors
// AuctionBiddingView.js's formatBidUnitPrice, keyed off the action's unit
// rather than a full action object since callers here already have it handy.
const formatBidUnitPrice = (bid, unit, amount) => {
    if (!unit || unit === 'farm') return null;
    const quantity = unit === 'ha' ? (parseFloat(bid.parcel?.area) || 0) : (bid.units || 0);
    if (!quantity) return null;
    const unitLabel = unit === 'ha' ? 'Ha' : unit;
    return `£${(amount / quantity).toFixed(1)}/${unitLabel}`;
};

const OddsBadge = ({ bidId, getOddsInfo }) => {
    const odds = getOddsInfo(bidId);
    return (
        <div>
            <span style={{ ...styles.oddsBadge, backgroundColor: odds.bg, borderColor: odds.border, color: odds.color }}>
                {odds.label}
            </span>
            {odds.checkedAtLabel && <div style={styles.oddsCheckedAt}>{odds.checkedAtLabel}</div>}
        </div>
    );
};

// Buckets an event's bids by action, with a quantity subtotal (parcel area
// for 'ha' actions, else summed units) and a cost subtotal per group.
const groupBidsByAction = (eventBids, events, eventId, isGrantStatus) => {
    const event = events.find(e => e._id === eventId);
    const groups = {};
    eventBids.forEach(bid => {
        const key = bid.selectedAction;
        if (!groups[key]) {
            groups[key] = {
                actionId: key,
                label: getFriendlyActionLabel(events, eventId, key),
                unit: event?.allowedActions.find(a => a.actionId === key)?.unit,
                bids: [],
                quantityTotal: 0,
                costTotal: 0
            };
        }
        const g = groups[key];
        g.bids.push(bid);
        g.costTotal += (bid.payoutAmount ?? bid.amount) || 0;
        if (g.unit === 'ha') {
            if (bid.parcel && bid.parcel.area) g.quantityTotal += parseFloat(bid.parcel.area) || 0;
        } else {
            g.quantityTotal += bid.units || 0;
        }
    });
    return Object.values(groups).sort((a, b) => a.label.localeCompare(b.label));
};

const formatGroupQuantity = (group) => {
    if (group.unit === 'ha') return `${group.quantityTotal.toFixed(2)} ha`;
    if (group.unit) return `${group.quantityTotal} ${group.unit}`;
    return '';
};

const formatGroupCost = (group, isGrantStatus) => {
    const amount = isGrantStatus ? Math.round(group.costTotal) : group.costTotal;
    return `£${amount.toLocaleString('en-GB')}`;
};

const AuctionStatusView = ({
    allBids, events, statusFilterName, setStatusFilterName, statusFilterState, setStatusFilterState,
    setSimEventData, setSimModalOpen,
    handleEditBid, handleDeleteBid, handleWithdrawBid,
    getStatusLabel, getBidStatusColor, getOddsInfo
}) => {
    const bidsByEvent = allBids.reduce((acc, bid) => {
        const evId = bid.event;
        if (!acc[evId]) acc[evId] = [];
        acc[evId].push(bid);
        return acc;
    }, {});

    const filteredKeys = Object.keys(bidsByEvent).filter(eventId => {
        const event = events.find(e => e._id === eventId);
        if (!event) return false;

        const isActive = event.status === 'active' && new Date(event.endDate) > new Date();

        if (statusFilterName && !event.name.toLowerCase().includes(statusFilterName.toLowerCase())) return false;
        if (statusFilterState === 'open' && !isActive) return false;
        if (statusFilterState === 'closed' && isActive) return false;

        return true;
    });

    return (
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px', backgroundColor:'white', padding:'15px', borderRadius:'8px', boxShadow:'0 1px 3px rgba(0,0,0,0.1)'}}>
            <div style={{display:'flex', gap:'15px', alignItems:'center'}}>
                <label style={{ ...styles.hHighlight, margin: 0 }}>Filter</label>
                <input
                    placeholder="Event Name…"
                    value={statusFilterName}
                    onChange={(e) => setStatusFilterName(e.target.value)}
                    style={{...styles.input, width:'200px', marginBottom:0, padding:'8px'}}
                />
                <select
                    value={statusFilterState}
                    onChange={(e) => setStatusFilterState(e.target.value)}
                    style={{...styles.select, width:'150px', marginBottom:0, padding:'8px'}}
                >
                    <option value="all" style={{ color: '#475569' }}>All Status</option>
                    <option value="open" style={{ color: '#0F172A' }}>Open</option>
                    <option value="closed" style={{ color: '#0F172A' }}>Closed</option>
                </select>
            </div>
            <div style={{color:'#888', fontSize:'0.9rem'}}>
                Showing {filteredKeys.length} Events
            </div>
        </div>

        {filteredKeys.map(eventId => {
            const event = events.find(e => e._id === eventId);
            if (!event) return null;

            const eventBids = bidsByEvent[eventId];
            const isActive = event.status === 'active' && new Date(event.endDate) > new Date();
            const isGrantStatus = event.auctionType === 'grants';
            const isManual = event.auctionType === 'manual';
            const isManuallyCancelled = event.closureType === 'manual_early';
            // Manually-closed auctions end before their originally-planned endDate,
            // so the actual closure timestamp (updatedAt) is the accurate date to show.
            const closeDateLabel = isManuallyCancelled && event.updatedAt
                ? new Date(event.updatedAt).toLocaleDateString('en-GB')
                : (event.endDate ? new Date(event.endDate).toLocaleDateString('en-GB') : '—');
            const closeDateFieldLabel = isManuallyCancelled ? 'Closed On' : 'Close Date';

            const confirmedTotal = eventBids
                .filter(b => b.status !== 'draft')
                .reduce((sum, b) => sum + ((b.payoutAmount ?? b.amount) || 0), 0);

            return (
                <div key={eventId} style={styles.card} className="mb-20">
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', borderBottom:'1px solid #eee', paddingBottom:'15px', marginBottom:'15px', flexWrap:'wrap', gap:'10px'}}>
                        <div style={{display:'flex', alignItems:'center', gap:'15px', flexWrap:'wrap'}}>
                            <h2 style={{margin:0, color: theme.colors.primary}}>{event.name}</h2>
                            <span style={{
                                padding:'4px 10px', borderRadius:'12px', fontSize:'0.85rem', fontWeight:'bold', fontFamily: '"Inter", sans-serif',
                                backgroundColor: isActive ? '#0D9488' : (isManuallyCancelled ? '#94A3B8' : '#0F172A'),
                                color: isActive ? '#FFFFFF' : '#F8FAFC'
                            }}>
                                {isActive ? 'Open' : (isManuallyCancelled ? 'Cancelled' : 'Closed')}
                            </span>
                            <span style={{fontSize:'0.85rem', color:'#475569', fontFamily: '"Inter", sans-serif'}}>
                                {closeDateFieldLabel}: {closeDateLabel}
                            </span>
                        </div>

                        {isActive && !isManual && (
                            <button
                              onClick={() => {
                                  setSimEventData({ id: eventId, name: event.name });
                                  setSimModalOpen(true);
                              }}
                              style={styles.btnPrimary}
                            >
                              Run Simulation (Winning or Losing?)
                            </button>
                        )}
                    </div>

                    {isManual ? (
                        <table style={{width:'100%', borderCollapse:'collapse', tableLayout:'fixed'}}>
                            <colgroup>
                                <col style={{width: isActive ? '18%' : '20%'}} />
                                <col style={{width: isActive ? '12%' : '13%'}} />
                                <col style={{width: isActive ? '12%' : '12%'}} />
                                <col style={{width: isActive ? '18%' : '20%'}} />
                                <col style={{width: isActive ? '25%' : '35%'}} />
                                {isActive && <col style={{width:'15%'}} />}
                            </colgroup>
                            <thead>
                                <tr style={{backgroundColor:'#f9f9f9', textAlign:'left', borderBottom:'2px solid #ddd'}}>
                                    <th style={styles.th}>Action</th>
                                    <th style={styles.th}>Price</th>
                                    <th style={styles.th}>Price per Unit</th>
                                    <th style={styles.th}>Status</th>
                                    <th style={styles.th}>Outcome Comments</th>
                                    {isActive && <th style={styles.th}>Manage Bid</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {eventBids.map(bid => {
                                    const isDraft = bid.status === 'draft';
                                    const isPending = bid.status === 'pending';
                                    // Type 5 (competitive_fundspreader) bids get their final per-hectare
                                    // payout computed only once the auction closes — payoutAmount, when
                                    // present, is the actual amount awarded and takes precedence over
                                    // the farmer's originally submitted amount.
                                    const displayAmount = bid.payoutAmount ?? bid.amount;
                                    const displayPrice = isGrantStatus ? Math.round(displayAmount).toLocaleString('en-GB') : displayAmount.toLocaleString('en-GB');
                                    const actionUnit = event.allowedActions.find(a => a.actionId === bid.selectedAction)?.unit;
                                    const unitPrice = formatBidUnitPrice(bid, actionUnit, displayAmount);

                                    return (
                                        <tr key={bid._id} style={{borderBottom:'1px solid #eee', opacity: isManuallyCancelled ? 0.55 : 1}}>
                                            <td style={styles.td}>{getFriendlyActionLabel(events, eventId, bid.selectedAction)}</td>
                                            <td style={styles.td}>£{displayPrice}</td>
                                            <td style={styles.td}>{unitPrice || '—'}</td>
                                            <td style={styles.td}>
                                                <span style={{
                                                    fontWeight:'bold', fontSize:'12px', fontFamily: '"Inter", sans-serif',
                                                    color: isManuallyCancelled ? '#94A3B8' : getBidStatusColor(bid.status)
                                                }}>
                                                    {isManuallyCancelled ? 'Event Cancelled' : getStatusLabel(bid.status)}
                                                </span>
                                            </td>
                                            <td style={styles.td}>
                                                {!isActive && bid.adminDecisionComments && (
                                                    <div style={{ fontSize: '11px', color: '#475569', maxWidth: '260px', whiteSpace: 'normal' }}>
                                                        {bid.adminDecisionComments}
                                                    </div>
                                                )}
                                            </td>
                                            {isActive && (
                                                <td style={styles.td}>
                                                    {isDraft && (
                                                        <div style={{display:'flex', gap:'5px', flexWrap:'wrap'}}>
                                                            <button onClick={() => handleEditBid(bid)} style={{...styles.btnSecondary, padding:'4px 8px', fontSize:'11px'}}>Revise</button>
                                                            <button onClick={() => handleDeleteBid(bid._id)} style={{...styles.btnDangerOutline, padding:'4px 8px', fontSize:'11px'}}>Delete</button>
                                                        </div>
                                                    )}
                                                    {isPending && (
                                                        <div style={{display:'flex', gap:'5px'}}>
                                                             <button onClick={() => handleEditBid(bid)} style={{...styles.btnSecondary, padding:'4px 8px', fontSize:'11px'}}>Revise</button>
                                                             <button onClick={() => handleWithdrawBid(bid)} style={{...styles.btnDanger, padding:'4px 8px', fontSize:'11px'}}>Withdraw</button>
                                                        </div>
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}

                                <tr style={{backgroundColor:'#163321', color: '#FFFFFF', borderTop:'2px solid #ddd'}}>
                                    <td style={{padding:'10px', textAlign:'right', fontWeight:'bold', fontFamily: '"Inter", sans-serif'}}>Total: (Submitted Bids)</td>
                                    <td style={{padding:'10px', fontWeight:'bold', fontFamily: '"Inter", sans-serif'}}>£{Math.round(confirmedTotal).toLocaleString('en-GB')}</td>
                                    <td colSpan={isActive ? 4 : 3}></td>
                                </tr>
                            </tbody>
                        </table>
                    ) : (
                        <table style={{width:'100%', borderCollapse:'collapse', tableLayout:'fixed'}}>
                            <colgroup>
                                <col style={{width: isActive ? '20%' : '26%'}} />
                                <col style={{width:'10%'}} />
                                <col style={{width:'10%'}} />
                                <col style={{width:'10%'}} />
                                <col style={{width: isActive ? '12%' : '12%'}} />
                                <col style={{width: isActive ? '12%' : '32%'}} />
                                {isActive && <col style={{width:'13%'}} />}
                                {isActive && <col style={{width:'13%'}} />}
                            </colgroup>
                            <thead>
                                <tr style={{backgroundColor:'#f9f9f9', textAlign:'left', borderBottom:'2px solid #ddd'}}>
                                    <th style={styles.th}>Field</th>
                                    <th style={styles.th}>Field Area</th>
                                    <th style={styles.th}>Quantity</th>
                                    <th style={styles.th}>Price</th>
                                    <th style={styles.th}>Price per Unit</th>
                                    <th style={styles.th}>Status</th>
                                    {isActive && <th style={styles.th}>Odds</th>}
                                    {isActive && <th style={styles.th}>Manage Bid</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {groupBidsByAction(eventBids, events, eventId, isGrantStatus).map(group => {
                                    const totalCols = isActive ? 8 : 6;
                                    return (
                                    <React.Fragment key={group.actionId}>
                                        <tr style={styles.groupHeaderRowTable}>
                                            <td colSpan={totalCols} style={styles.groupHeaderCellTable}>Action: {group.label}</td>
                                        </tr>
                                        {group.bids.map(bid => {
                                            const isDraft = bid.status === 'draft';
                                            const isPending = bid.status === 'pending';
                                            const displayStatusAmount = bid.payoutAmount ?? bid.amount;
                                            const displayStatusPrice = isGrantStatus ? Math.round(displayStatusAmount).toLocaleString('en-GB') : displayStatusAmount.toLocaleString('en-GB');

                                            return (
                                                <tr key={bid._id} style={{borderBottom:'1px solid #eee', opacity: isManuallyCancelled ? 0.55 : 1}}>
                                                    <td style={styles.td}>
                                                        <div style={{ fontWeight: 'bold', fontSize: '12px' }}>
                                                            <span style={{ color: bid.parcel?.customName ? '#0D9488' : '#0F172A' }}>
                                                                {bid.parcel ? (bid.parcel.customName || bid.parcel.parcelRef) : 'Whole Farm'}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td style={styles.td}>
                                                        {bid.parcel ? (bid.parcel.area ? Number(bid.parcel.area).toFixed(2) + ' ha' : '- ha') : 'NA'}
                                                    </td>

                                                    <td style={styles.td}>{getUnitInfo(events, eventId, bid.selectedAction, bid.units)}</td>
                                                    <td style={styles.td}>£{displayStatusPrice}</td>
                                                    <td style={styles.td}>{formatBidUnitPrice(bid, group.unit, displayStatusAmount) || '—'}</td>
                                                    <td style={styles.td}>
                                                        <span style={{
                                                            fontWeight:'bold', fontSize:'12px', fontFamily: '"Inter", sans-serif',
                                                            color: isManuallyCancelled ? '#94A3B8' : getBidStatusColor(bid.status)
                                                        }}>
                                                            {isManuallyCancelled ? 'Event Cancelled' : getStatusLabel(bid.status)}
                                                        </span>
                                                    </td>
                                                    {isActive && (
                                                        <td style={styles.td}>
                                                            <OddsBadge bidId={bid._id} getOddsInfo={getOddsInfo} />
                                                        </td>
                                                    )}
                                                    {isActive && (
                                                        <td style={styles.td}>
                                                            {isDraft && (
                                                                <div style={{display:'flex', gap:'5px', flexWrap:'wrap'}}>
                                                                    <button onClick={() => handleEditBid(bid)} style={{...styles.btnSecondary, padding:'4px 8px', fontSize:'11px'}}>Revise</button>
                                                                    <button onClick={() => handleDeleteBid(bid._id)} style={{...styles.btnDangerOutline, padding:'4px 8px', fontSize:'11px'}}>Delete</button>
                                                                </div>
                                                            )}
                                                            {isPending && (
                                                                <div style={{display:'flex', gap:'5px'}}>
                                                                     <button onClick={() => handleEditBid(bid)} style={{...styles.btnSecondary, padding:'4px 8px', fontSize:'11px'}}>Revise</button>
                                                                     <button onClick={() => handleWithdrawBid(bid)} style={{...styles.btnDanger, padding:'4px 8px', fontSize:'11px'}}>Withdraw</button>
                                                                </div>
                                                            )}
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                        <tr style={styles.groupSubtotalRowTable}>
                                            <td style={styles.groupSubtotalCellTable}>Subtotal</td>
                                            <td style={styles.groupSubtotalCellTable}>{group.unit === 'ha' ? formatGroupQuantity(group) : ''}</td>
                                            <td style={styles.groupSubtotalCellTable}>{group.unit !== 'ha' ? formatGroupQuantity(group) : ''}</td>
                                            <td style={styles.groupSubtotalCellTable}>{formatGroupCost(group, isGrantStatus)}</td>
                                            <td style={styles.groupSubtotalCellTable}></td>
                                            <td colSpan={isActive ? 3 : 1} style={styles.groupSubtotalCellTable}></td>
                                        </tr>
                                    </React.Fragment>
                                    );
                                })}

                                <tr style={{backgroundColor:'#163321', color: '#FFFFFF', borderTop:'2px solid #ddd'}}>
                                    <td colSpan="3" style={{padding:'10px', textAlign:'right', fontWeight:'bold', fontFamily: '"Inter", sans-serif'}}>Total: (Submitted Bids)</td>
                                    <td style={{padding:'10px', fontWeight:'bold', fontFamily: '"Inter", sans-serif'}}>£{Math.round(confirmedTotal).toLocaleString('en-GB')}</td>
                                    <td colSpan={isActive ? 4 : 2}></td>
                                </tr>
                            </tbody>
                        </table>
                    )}
                </div>
            );
        })}
        {filteredKeys.length === 0 && (
            <div style={{textAlign:'center', padding:'40px', color:'#999'}}>No events found matching criteria.</div>
        )}
    </div>
    );
};

export default AuctionStatusView;
